<template>
  <!--
    Account side drawer. Slides in from the right edge over a backdrop when the
    header avatar is tapped (see AppHeader → menuController.open('profile-menu')).
    Swipe-to-open is disabled so it's only reachable from authenticated screens
    via the avatar — never by an edge swipe on the sign-in / onboarding pages,
    which share the same root router outlet this menu is bound to.
  -->
  <ion-menu
    menu-id="profile-menu"
    content-id="main-content"
    side="end"
    type="overlay"
    :swipe-gesture="false"
    class="side-menu"
    @ion-will-open="onMenuWillOpen"
  >
    <ion-content class="side-menu__content">
      <!-- ─── Identity card ─────────────────────────────────────────── -->
      <header class="side-menu__head">
        <div class="side-menu__avatar" aria-hidden="true">
          <img
            v-if="showImage"
            :src="userStore.image"
            alt=""
            class="side-menu__avatar-img"
            @error="imgFailed = true"
          />
          <span v-else-if="userStore.initials">{{ userStore.initials }}</span>
          <ion-icon v-else :icon="personOutline" class="side-menu__avatar-icon" />
        </div>
        <p class="side-menu__name">{{ userStore.name || 'Welcome' }}</p>
        <p v-if="userStore.role" class="side-menu__role">{{ userStore.role }}</p>
        <p v-if="userStore.email" class="side-menu__email">{{ userStore.email }}</p>
        <!-- Gated on what is actually rendered below. This used to test
             `employmentType`, which is a different (and unreliable) field — it
             falls back to `user_type`, i.e. "Staff" — so the stage chip vanished
             whenever that happened to be empty. -->
        <div
          v-if="userStore.status || userStore.employmentStage"
          class="side-menu__badges"
        >
          <span
            v-if="userStore.status"
            class="side-menu__badge side-menu__badge--active"
          >{{ userStore.status }}</span>
          <span
            v-if="userStore.employmentStage"
            class="side-menu__badge side-menu__badge--type"
          >{{ userStore.employmentStage }}</span>
        </div>
      </header>

      <!-- ─── Personal preference ───────────────────────────────────── -->
      <section class="side-menu__prefs" aria-label="Personal preference">
        <p class="side-menu__section-title">Personal Preference</p>
        <div class="side-menu__pref-group" role="list">
          <div class="side-menu__pref-row" role="listitem">
            <span class="side-menu__pref-icon-wrap" aria-hidden="true">
              <ion-icon :icon="notificationsOutline" class="side-menu__pref-icon" />
            </span>
            <div class="side-menu__pref-content">
              <p class="side-menu__pref-label">Notifications</p>
              <p class="side-menu__pref-desc">Choose what you want to be notified about.</p>
            </div>
            <label
              class="side-menu__toggle"
              :aria-label="preferences.notifications ? 'Notifications on' : 'Notifications off'"
            >
              <input
                v-model="preferences.notifications"
                type="checkbox"
                class="side-menu__toggle-input"
                @change="onNotificationsToggle"
              />
              <span class="side-menu__toggle-track" />
            </label>
            <!-- The toggle keeps its own in-app preference; the chevron opens the
                 device-notification / live-updates settings page. -->
            <button
              type="button"
              class="side-menu__pref-chevron-btn"
              aria-label="Notification settings"
              @click="openNotificationSettings"
            >
              <ion-icon :icon="chevronForwardOutline" class="side-menu__pref-chevron" aria-hidden="true" />
            </button>
          </div>

          <div
            class="side-menu__pref-row side-menu__pref-row--bordered side-menu__pref-row--theme"
            role="listitem"
          >
            <span class="side-menu__pref-icon-wrap" aria-hidden="true">
              <ion-icon :icon="moonOutline" class="side-menu__pref-icon" />
            </span>
            <div class="side-menu__pref-content">
              <p class="side-menu__pref-label">Appearance</p>
              <p class="side-menu__pref-desc">
                <template v-if="settingsStore.themeMode === 'system'">Following system preference</template>
                <template v-else-if="settingsStore.themeMode === 'dark'">Dark mode enabled</template>
                <template v-else>Light mode enabled</template>
              </p>
            </div>
            <div class="side-menu__theme-segment" role="group" aria-label="Theme mode">
              <button
                v-for="opt in themeOptions"
                :key="opt.value"
                type="button"
                class="side-menu__theme-btn"
                :class="{ 'side-menu__theme-btn--active': settingsStore.themeMode === opt.value }"
                :aria-pressed="settingsStore.themeMode === opt.value"
                :aria-label="opt.label"
                @click="onThemeSelect(opt.value)"
              >
                <ion-icon :icon="opt.icon" class="side-menu__theme-icon" aria-hidden="true" />
                <span class="side-menu__theme-label">{{ opt.label }}</span>
              </button>
            </div>
          </div>

          <!-- Language — opens the language picker over the app -->
          <button
            type="button"
            class="side-menu__pref-row side-menu__pref-row--bordered side-menu__pref-row--action"
            role="listitem"
            aria-label="Language"
            @click="openLanguage"
          >
            <span class="side-menu__pref-icon-wrap" aria-hidden="true">
              <ion-icon :icon="globeOutline" class="side-menu__pref-icon" />
            </span>
            <div class="side-menu__pref-content">
              <p class="side-menu__pref-label">Language</p>
              <p class="side-menu__pref-desc">{{ currentLanguageLabel }}</p>
            </div>
            <ion-icon :icon="chevronForwardOutline" class="side-menu__pref-chevron" aria-hidden="true" />
          </button>

          <!-- Password — full reset-password page -->
          <button
            type="button"
            class="side-menu__pref-row side-menu__pref-row--bordered side-menu__pref-row--action"
            role="listitem"
            aria-label="Password"
            @click="openPassword"
          >
            <span class="side-menu__pref-icon-wrap" aria-hidden="true">
              <ion-icon :icon="lockClosedOutline" class="side-menu__pref-icon" />
            </span>
            <div class="side-menu__pref-content">
              <p class="side-menu__pref-label">Password</p>
              <p class="side-menu__pref-desc">Change your account password.</p>
            </div>
            <ion-icon :icon="chevronForwardOutline" class="side-menu__pref-chevron" aria-hidden="true" />
          </button>

          <!-- Security PIN — hidden on an ERPat that 404s the feature. The chip
               states On / Off / Locked in words (never colour alone), and the
               accessible name carries the same state. -->
          <button
            v-if="showSecurityPinRow"
            type="button"
            class="side-menu__pref-row side-menu__pref-row--bordered side-menu__pref-row--action"
            role="listitem"
            :aria-label="securityPinState ? `Security PIN, ${securityPinState.label}` : 'Security PIN'"
            @click="openSecurityPin"
          >
            <span class="side-menu__pref-icon-wrap" aria-hidden="true">
              <ion-icon :icon="keypadOutline" class="side-menu__pref-icon" />
            </span>
            <div class="side-menu__pref-content">
              <p class="side-menu__pref-label">Security PIN</p>
              <p class="side-menu__pref-desc">Unlock your pay and IDs with a 4-digit PIN.</p>
            </div>
            <span
              v-if="securityPinState"
              class="side-menu__pref-chip"
              :class="`side-menu__pref-chip--${securityPinState.tone}`"
              aria-hidden="true"
            >{{ securityPinState.label }}</span>
            <ion-icon :icon="chevronForwardOutline" class="side-menu__pref-chevron" aria-hidden="true" />
          </button>
        </div>
      </section>

      <!-- ─── Navigation ────────────────────────────────────────────── -->
      <nav class="side-menu__nav" aria-label="Account menu">
        <button
          v-for="item in items"
          :key="item.id"
          type="button"
          class="side-menu__item"
          @click="go(item.route)"
        >
          <span class="side-menu__item-icon-wrap" aria-hidden="true">
            <ion-icon :icon="item.icon" class="side-menu__item-icon" />
          </span>
          <span class="side-menu__item-label">{{ item.label }}</span>
          <ion-icon :icon="chevronForwardOutline" class="side-menu__item-chevron" aria-hidden="true" />
        </button>

        <!-- Sync — the punches saved on this device (offline attendance). The
             chip says, in words, how many still wait to sync (orange) or need
             review (red); hidden at 0. The accessible name carries the same. -->
        <button
          type="button"
          class="side-menu__item"
          :aria-label="syncRowLabel"
          @click="go('/sync-issues')"
        >
          <span class="side-menu__item-icon-wrap" aria-hidden="true">
            <ion-icon :icon="syncOutline" class="side-menu__item-icon" />
          </span>
          <span class="side-menu__item-label">{{ syncLabel }}</span>
          <span
            v-if="syncChip"
            class="side-menu__pref-chip"
            :class="`side-menu__pref-chip--${syncChip.tone}`"
            aria-hidden="true"
          >{{ syncChip.text }}</span>
          <ion-icon :icon="chevronForwardOutline" class="side-menu__item-chevron" aria-hidden="true" />
        </button>
      </nav>

      <!-- ─── Footer ────────────────────────────────────────────────── -->
      <div class="side-menu__footer">
        <button
          type="button"
          class="side-menu__sign-out"
          :disabled="signingOut"
          @click="openSignOutConfirm"
        >
          <span class="side-menu__sign-out-icon-wrap" aria-hidden="true">
            <ion-icon :icon="logOutOutline" class="side-menu__sign-out-icon" />
          </span>
          <span class="side-menu__sign-out-copy">
            <span class="side-menu__sign-out-label">Sign Out</span>
            <span class="side-menu__sign-out-desc">Leave this device.</span>
          </span>
          <ion-icon :icon="chevronForwardOutline" class="side-menu__sign-out-chevron" aria-hidden="true" />
        </button>
        <p class="side-menu__version">Syntry • v{{ appVersion }}</p>
      </div>
    </ion-content>

    <!-- ─── Language picker (opened from the Language preference row) ─── -->
    <ion-modal
      :is-open="showLanguageModal"
      css-class="language-modal-sheet"
      @did-dismiss="showLanguageModal = false"
    >
      <LanguageModal
        :current="settingsStore.language"
        @select="onLanguageSelect"
        @close="showLanguageModal = false"
      />
    </ion-modal>

    <!-- ─── Sign out confirmation ──────────────────────────────────── -->
    <ion-modal
      :is-open="showSignOutModal"
      css-class="sign-out-modal-sheet"
      @did-dismiss="showSignOutModal = false"
    >
      <div
        class="sign-out-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="side-sign-out-title"
      >
        <div class="sign-out-modal__icon-ring" aria-hidden="true">
          <ion-icon :icon="logOutOutline" class="sign-out-modal__icon" />
        </div>
        <h2 id="side-sign-out-title" class="sign-out-modal__title">Sign Out?</h2>
        <p class="sign-out-modal__desc">
          You will return to the sign-in screen and need to sign in again to access Syntry.
        </p>
        <!-- Punches saved offline stay on this device and sync on the next
             sign-in with this account — say so before the employee leaves. -->
        <p v-if="signOutPendingNote" class="sign-out-modal__pending" role="note">
          <ion-icon :icon="syncOutline" aria-hidden="true" />
          <span>{{ signOutPendingNote }}</span>
        </p>
        <div class="sign-out-modal__actions">
          <button
            type="button"
            class="sign-out-modal__cancel-btn"
            :disabled="signingOut"
            @click="showSignOutModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="sign-out-modal__confirm-btn"
            :disabled="signingOut"
            @click="confirmSignOut"
          >
            {{ signingOut ? 'Signing Out...' : 'Sign Out' }}
          </button>
        </div>
      </div>
    </ion-modal>
  </ion-menu>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { IonMenu, IonContent, IonIcon, IonModal, menuController } from '@ionic/vue';
import {
  personOutline,
  notificationsOutline,
  chevronForwardOutline,
  moonOutline,
  sunnyOutline,
  phonePortraitOutline,
  globeOutline,
  lockClosedOutline,
  keypadOutline,
  logOutOutline,
  syncOutline,
} from 'ionicons/icons';
import LanguageModal from '@/components/modals/LanguageModal.vue';
import { useUserStore } from '@/stores/user.store';
import { useSessionStore } from '@/stores/session.store';
import { useSecurityStore } from '@/stores/security.store';
import { useSettingsStore } from '@/stores/settings.store';
import { useSyncStore } from '@/stores/sync.store';
import { offlineAttendanceCopy, signOutPendingCopy, syncBadgeCopy } from '@/i18n/offline-attendance.en';
import type { ThemeMode, LanguageCode } from '@/stores/settings.store';
import { preferencesService } from '@/services/erpat';
import { APP_VERSION } from '@/utils/app-info';

const router = useRouter();
const userStore = useUserStore();
const sessionStore = useSessionStore();
const settingsStore = useSettingsStore();

const appVersion = APP_VERSION;

// ── Personal preference ───────────────────────────────────────────────
const themeOptions: { value: ThemeMode; label: string; icon: string }[] = [
  { value: 'system', label: 'Auto',  icon: phonePortraitOutline },
  { value: 'light',  label: 'Light', icon: sunnyOutline },
  { value: 'dark',   label: 'Dark',  icon: moonOutline },
];

// The toggle stays local UI state; we seed it from the server (best-effort) and
// push changes back without blocking the UI on the round-trip.
const preferences = reactive({
  notifications: true,
});

// This drawer is mounted app-wide (App.vue), so it also exists on the sign-in
// screen. Only read preferences once a session actually exists — firing it
// while signed out produces a pointless unauthenticated /me/preferences call.
// Re-runs on every sign-in, so a fresh session re-seeds the toggle.
watch(
  () => sessionStore.isAuthenticated,
  (authed) => {
    if (!authed) return;
    void preferencesService
      .get()
      .then((prefs) => { preferences.notifications = prefs.notifications; })
      .catch(() => { /* keep the default — server sync is best-effort */ });
  },
  { immediate: true },
);

async function onNotificationsToggle(): Promise<void> {
  try {
    await preferencesService.update({ notifications: preferences.notifications });
  } catch {
    // Best-effort sync; the local toggle is still honoured.
  }
}

/** Close the drawer first (awaiting the animation), then open the full page. */
async function openNotificationSettings(): Promise<void> {
  await menuController.close('profile-menu');
  void router.push('/notification-settings');
}

/** Apply the theme locally (the store is the source of truth), then sync. */
async function onThemeSelect(mode: ThemeMode): Promise<void> {
  settingsStore.setThemeMode(mode);
  try {
    await preferencesService.update({ theme: mode });
  } catch {
    // Best-effort server sync; local theme already applied + persisted.
  }
}

// ── Language & Password preferences ───────────────────────────────────
const showLanguageModal = ref(false);

const LANGUAGE_LABELS: Record<LanguageCode, string> = {
  en: 'English',
  fil: 'Filipino',
  ceb: 'Cebuano',
  ilo: 'Ilocano',
  hil: 'Hiligaynon',
};

const currentLanguageLabel = computed(
  () => LANGUAGE_LABELS[settingsStore.language] ?? 'English',
);

/** Close the drawer, then surface the language picker over the app. */
async function openLanguage(): Promise<void> {
  await menuController.close('profile-menu');
  showLanguageModal.value = true;
}

function onLanguageSelect(code: LanguageCode): void {
  settingsStore.setLanguage(code);
  showLanguageModal.value = false;
}

/** Close the drawer first (awaiting the animation), then navigate. */
async function openPassword(): Promise<void> {
  await menuController.close('profile-menu');
  void router.push('/reset-password');
}

// ── Security PIN ──────────────────────────────────────────────────────
// The status is read by the session store on every sign-in / restore; the
// drawer only displays it. Shown while the backend is known to support the PIN
// OR has not answered yet — only a settled 404 (`supported === false`) hides it.
const security = useSecurityStore();

const showSecurityPinRow = computed(
  () => sessionStore.isAuthenticated && security.supported !== false,
);

/** The chip's words + semantic tone; null while the status is unknown. */
const securityPinState = computed((): { label: string; tone: 'on' | 'off' | 'locked' } | null => {
  const s = security.status;
  if (!s) return null;
  if (s.available && s.locked) return { label: 'Locked', tone: 'locked' };
  if (s.available && s.enabled) return { label: 'On', tone: 'on' };
  return { label: 'Off', tone: 'off' };
});

/**
 * Opening the drawer re-probes a status that never settled (a sign-in whose
 * status read failed). A no-op once the status is known or a read is in flight.
 */
function onMenuWillOpen(): void {
  if (sessionStore.isAuthenticated) void security.ensureLoaded();
}

/** Close the drawer first (awaiting the animation), then navigate. */
async function openSecurityPin(): Promise<void> {
  await menuController.close('profile-menu');
  void router.push('/security-pin');
}

// Avatar image with an initials fallback, mirroring AppHeader: reset the failure
// flag on every successful profile load (store `version` tick) so a transient
// image error self-heals even when the refreshed URL is unchanged.
const imgFailed = ref(false);
watch(() => userStore.version, () => { imgFailed.value = false; });
const showImage = computed(() => Boolean(userStore.image) && !imgFailed.value);

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  route: string;
}

// Keep the account drawer focused; app/help/legal links live in AppInfoMenu.
const items: MenuItem[] = [
  { id: 'profile', label: 'View Profile', icon: personOutline, route: '/profile' },
];

/** Close the drawer first (awaiting the animation) before navigating. */
async function go(route: string): Promise<void> {
  await menuController.close('profile-menu');
  void router.push(route);
}

// ── Sync (offline attendance) ─────────────────────────────────────────
// Counts come from the sync store, which the offline-attendance orchestrator
// fills for the signed-in session (and resets at sign-out).
const syncStore = useSyncStore();
const syncLabel = offlineAttendanceCopy('menu.sync');

/** The chip: needs review (red) outranks waiting (orange); null when nothing is unsettled. */
const syncChip = computed((): { text: string; label: string; tone: 'review' | 'pending' } | null => {
  if (syncStore.reviewCount > 0) return { ...syncBadgeCopy('review', syncStore.reviewCount), tone: 'review' };
  if (syncStore.pendingCount > 0) return { ...syncBadgeCopy('pending', syncStore.pendingCount), tone: 'pending' };
  return null;
});

const syncRowLabel = computed(() => (syncChip.value ? `${syncLabel}, ${syncChip.value.label}` : syncLabel));

/** "2 punches haven't synced yet. They stay on this device…" — only while some wait. */
const signOutPendingNote = computed(() =>
  syncStore.pendingCount > 0 ? signOutPendingCopy(syncStore.pendingCount) : '',
);

const showSignOutModal = ref(false);
const signingOut = ref(false);

async function openSignOutConfirm(): Promise<void> {
  await menuController.close('profile-menu');
  showSignOutModal.value = true;
}

async function confirmSignOut(): Promise<void> {
  if (signingOut.value) return;
  signingOut.value = true;
  try {
    await sessionStore.logout();
  } finally {
    signingOut.value = false;
    showSignOutModal.value = false;
    await router.replace('/auth/sign-in');
  }
}
</script>

<!-- Non-scoped: the Ionic modal portal renders outside this component's scope -->
<style lang="scss">
ion-modal.language-modal-sheet {
  --width: 90%;
  --max-width: 420px;
  --height: auto;
  --max-height: 86vh;
  --border-radius: var(--syntry-radius-modal);
  --backdrop-opacity: 0.5;
  --box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
  align-items: center;
}

ion-modal.language-modal-sheet .ion-page {
  position: relative;
  contain: content;
  min-height: unset;
  overflow-y: auto;
}

ion-modal.sign-out-modal-sheet {
  --width: 90%;
  --max-width: 380px;
  --height: auto;
  --border-radius: var(--syntry-radius-modal);
  --backdrop-opacity: 0.5;
  --box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
  align-items: center;
}
</style>

<style scoped lang="scss">
.side-menu {
  --width: 84%;
  --max-width: 360px;

  &__content {
    --background: var(--syntry-bg);

    &::part(scroll) {
      min-height: 100%;
      display: flex;
      flex-direction: column;
    }
  }

  /* ── Identity card (mirrors the ProfilePage profile card) ──────── */
  &__head {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: var(--sp-1);
    padding: calc(var(--syntry-safe-top, env(safe-area-inset-top, 0px)) + var(--sp-5)) var(--sp-4) var(--sp-5);
    background: var(--syntry-gradient-primary);
    color: #fff;
  }

  &__avatar {
    width: 64px;
    height: 64px;
    margin-bottom: var(--sp-2);
    flex-shrink: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.25);
    border: 3px solid rgba(255, 255, 255, 0.7);
    color: #fff;
    font-size: 18px;
    font-weight: 800;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  &__avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }

  &__avatar-icon {
    font-size: 30px;
    color: #fff;
  }

  &__name {
    margin: 0;
    font-size: var(--syntry-fs-h2);
    font-weight: 800;
    line-height: 1.2;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__role {
    margin: 2px 0 0;
    font-size: var(--syntry-fs-small);
    opacity: 0.88;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__email {
    margin: 3px 0 0;
    font-size: var(--syntry-fs-tiny);
    opacity: 0.75;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__badges {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: var(--sp-2);
    margin-top: var(--sp-3);
  }

  &__badge {
    padding: 4px var(--sp-3);
    border-radius: var(--syntry-radius-chip);
    font-size: 11px;
    font-weight: 600;

    &--active {
      background: rgba(255, 255, 255, 0.22);
      color: #fff;
      border: 1px solid rgba(255, 255, 255, 0.4);
    }

    &--type {
      background: rgba(255, 255, 255, 0.12);
      color: rgba(255, 255, 255, 0.9);
      border: 1px solid rgba(255, 255, 255, 0.25);
    }
  }

  /* ── Personal preference ───────────────────────────────────────── */
  &__prefs {
    padding: var(--sp-4) var(--sp-3) 0;
  }

  &__section-title {
    margin: 0 0 var(--sp-2);
    padding: 0 var(--sp-1);
    font-size: var(--syntry-fs-small);
    font-weight: 700;
    color: var(--syntry-text-strong);
  }

  &__pref-group {
    background: var(--syntry-surface);
    border-radius: var(--syntry-radius-card);
    box-shadow: var(--syntry-shadow-card);
    overflow: hidden;
  }

  &__pref-row {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    padding: var(--sp-3);
    min-height: 60px;

    &--bordered {
      border-top: 1px solid var(--syntry-border);
    }

    &--theme {
      flex-wrap: wrap;
      row-gap: var(--sp-3);
    }

    /* Tappable preference row rendered as a <button> (Language, Password, Security PIN). */
    &--action {
      width: 100%;
      background: none;
      border: none;
      font-family: inherit;
      text-align: left;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;

      &:active { background: var(--syntry-bg-soft); }
    }
  }

  /* These tiles are generic UI affordances (bell, moon, globe, lock), not the
     Syntry mark, so they follow the tenant brand color. Default tenants are
     unaffected: --syntry-mark/-soft and --syntry-primary/-light hold identical
     values in tokens.scss, light and dark alike — only an overridden palette
     (src/theme/brand-color.ts) makes them diverge. The wordmark, the avatar and
     the tab bar keep --syntry-mark and stay teal for every tenant. */
  &__pref-icon-wrap {
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    border-radius: var(--syntry-radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--syntry-brand-soft-surface, var(--syntry-primary-light));
  }

  &__pref-icon {
    font-size: 19px;
    color: var(--syntry-brand-soft-foreground, var(--syntry-primary));
  }

  &__pref-content {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__pref-label {
    margin: 0 0 2px;
    font-size: var(--syntry-fs-small);
    font-weight: 600;
    color: var(--syntry-text-strong);
    line-height: 1.3;
  }

  &__pref-desc {
    margin: 0;
    font-size: var(--syntry-fs-tiny);
    color: var(--syntry-text-muted);
    line-height: 1.4;
  }

  &__pref-chevron {
    font-size: 18px;
    color: var(--syntry-text-soft);
    flex-shrink: 0;
  }

  /* Security PIN state chip. The word carries the state; the tint is the
     semantic status hue (on = green, locked = needs attention), never brand
     decoration. */
  &__pref-chip {
    flex-shrink: 0;
    padding: 3px var(--sp-2);
    border-radius: var(--syntry-radius-chip);
    font-size: 11px;
    font-weight: 800;
    white-space: nowrap;

    &--on     { background: var(--syntry-green-soft);  color: var(--syntry-green); }
    &--off    { background: var(--syntry-bg-soft);     color: var(--syntry-text-muted); }
    &--locked { background: var(--syntry-orange-soft); color: var(--syntry-orange); }
    /* Sync row: punches waiting to sync (orange) / needing review (red). */
    &--pending { background: var(--syntry-orange-soft); color: var(--syntry-orange); }
    &--review  { background: var(--syntry-red-soft);    color: var(--syntry-red); }
  }

  &__pref-chevron-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    min-width: 32px;
    min-height: 44px;
    padding: var(--sp-1);
    background: none;
    border: none;
    color: var(--syntry-text-soft);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  /* Notifications toggle */
  &__toggle {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    min-width: 44px;
    min-height: 44px;
    cursor: pointer;
  }

  &__toggle-input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    pointer-events: none;
  }

  &__toggle-track {
    display: block;
    width: 48px;
    height: 28px;
    border-radius: var(--syntry-radius-chip);
    background: var(--syntry-border);
    transition: background 200ms ease;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: 3px;
      left: 3px;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: #fff;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
      transition: transform 200ms ease;
    }
  }

  &__toggle-input:checked + &__toggle-track {
    background: var(--syntry-primary);

    &::after {
      transform: translateX(20px);
    }
  }

  /* Theme segmented control */
  &__theme-segment {
    display: flex;
    gap: var(--sp-1);
    width: 100%;
    flex: 1 1 100%;
    background: #00000020;
    border-radius: var(--syntry-radius-md);
    padding: 3px;
  }

  &__theme-btn {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: var(--sp-2) var(--sp-3);
    min-width: 0;
    min-height: 44px;
    border: none;
    background: transparent;
    border-radius: calc(var(--syntry-radius-md) - 3px);
    color: var(--syntry-text-muted);
    cursor: pointer;
    transition: background 180ms ease, color 180ms ease;

    &--active {
      background: var(--syntry-surface);
      color: var(--syntry-primary);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    }
  }

  &__theme-icon {
    font-size: 16px;
  }

  &__theme-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.02em;
  }

  /* ── Navigation ────────────────────────────────────────────────── */
  &__nav {
    padding: var(--sp-3) var(--sp-3) 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    width: 100%;
    padding: var(--sp-3);
    min-height: 52px;
    background: none;
    border: none;
    border-radius: var(--syntry-radius-md);
    cursor: pointer;
    text-align: left;
    -webkit-tap-highlight-color: transparent;

    &:active {
      background: var(--syntry-bg-soft);
    }

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }
  }

  /* Brand-following, matching the preference tiles above. */
  &__item-icon-wrap {
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    border-radius: var(--syntry-radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--syntry-brand-soft-surface, var(--syntry-primary-light));
  }

  &__item-icon {
    font-size: 19px;
    color: var(--syntry-brand-soft-foreground, var(--syntry-primary));
  }

  &__item-label {
    flex: 1;
    min-width: 0;
    font-size: var(--syntry-fs-small);
    font-weight: 600;
    color: var(--syntry-text-strong);

    &--danger {
      color: var(--syntry-red);
    }
  }

  &__item-chevron {
    font-size: 18px;
    color: var(--syntry-text-soft);
    flex-shrink: 0;
  }

  /* ── Footer ────────────────────────────────────────────────────── */
  &__footer {
    margin-top: auto;
    padding: var(--sp-3) var(--sp-3) calc(var(--sp-5) + env(safe-area-inset-bottom, 0px));
    border-top: 1px solid var(--syntry-border);
  }

  &__version {
    margin: var(--sp-3) 0 0;
    text-align: center;
    font-size: var(--syntry-fs-tiny);
    color: var(--syntry-text-soft);
  }

  &__sign-out {
    display: flex;
    align-items: center;
    gap: var(--sp-3);
    width: 100%;
    margin-top: 0;
    padding: var(--sp-3);
    min-height: 56px;
    background: transparent;
    border: none;
    border-radius: var(--syntry-radius-md);
    box-shadow: none;
    cursor: pointer;
    text-align: left;
    -webkit-tap-highlight-color: transparent;

    &:active {
      background: var(--syntry-red-soft);
    }

    &:disabled {
      opacity: 0.65;
      cursor: default;
    }
  }

  &__sign-out-icon-wrap {
    width: 38px;
    height: 38px;
    flex-shrink: 0;
    border-radius: var(--syntry-radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--syntry-red-soft);
  }

  &__sign-out-icon {
    font-size: 19px;
    color: var(--syntry-red);
  }

  &__sign-out-copy {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__sign-out-label {
    font-size: var(--syntry-fs-small);
    font-weight: 700;
    color: var(--syntry-red);
    line-height: 1.3;
  }

  &__sign-out-desc {
    font-size: var(--syntry-fs-tiny);
    color: var(--syntry-text-muted);
    line-height: 1.4;
  }

  &__sign-out-chevron {
    font-size: 18px;
    color: var(--syntry-red);
    opacity: 0.55;
    flex-shrink: 0;
  }
}

.sign-out-modal {
  background: var(--syntry-surface);
  border-radius: var(--syntry-radius-modal);
  padding: var(--sp-7) var(--sp-6) var(--sp-6);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &__icon-ring {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: var(--syntry-red-soft);
    border: 3px solid rgba(239, 68, 68, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: var(--sp-4);
  }

  &__icon {
    font-size: 38px;
    color: var(--syntry-red);
  }

  &__title {
    margin: 0 0 var(--sp-2);
    font-size: 22px;
    font-weight: 800;
    color: var(--syntry-text-strong);
    line-height: 1.2;
  }

  &__desc {
    margin: 0 0 var(--sp-5);
    font-size: var(--syntry-fs-small);
    color: var(--syntry-text-muted);
    line-height: 1.5;
  }

  /* Punches still waiting to sync: needs attention (orange), with the words. */
  &__pending {
    display: flex;
    align-items: flex-start;
    gap: var(--sp-2);
    margin: calc(-1 * var(--sp-3)) 0 var(--sp-5);
    padding: var(--sp-3);
    border-radius: var(--syntry-radius-md);
    background: var(--syntry-orange-soft);
    color: var(--syntry-text-strong);
    font-size: var(--syntry-fs-tiny);
    line-height: 1.5;
    text-align: left;

    ion-icon {
      flex-shrink: 0;
      margin-top: 2px;
      font-size: 15px;
      color: var(--syntry-orange);
    }
  }

  &__actions {
    display: flex;
    gap: var(--sp-3);
    width: 100%;
  }

  &__cancel-btn,
  &__confirm-btn {
    height: 50px;
    border-radius: var(--syntry-radius-button);
    font-size: var(--syntry-fs-small);
    font-weight: 700;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  &__cancel-btn {
    flex: 1;
    border: 1.5px solid var(--syntry-border);
    background: var(--syntry-surface);
    color: var(--syntry-text-strong);
  }

  &__confirm-btn {
    flex: 1.2;
    border: none;
    background: var(--syntry-red);
    color: #fff;
    box-shadow: 0 10px 22px rgba(239, 68, 68, 0.24);
  }

  &__cancel-btn:disabled,
  &__confirm-btn:disabled {
    opacity: 0.6;
    cursor: default;
  }
}
</style>
